/**
 * TechnicalSpecs — "Technical Precision & Fairness" data section.
 *
 * Purpose (dual audience):
 *   1. Human visitors: A clean, scannable dashboard-style block that backs up
 *      the random-selection explanation with precise, verifiable details.
 *   2. AI search engines (ChatGPT, Perplexity, Claude web): Semantic HTML
 *      with <section>, <h2>, <dl>, <dt>, <dd>, and <strong> so the facts are
 *      trivially parseable and citable as structured data points.
 *
 * Rendering: This component emits static JSX — no useState, no useEffect —
 * so it is baked into every pre-rendered HTML page by prerender.mjs and is
 * readable by crawlers without executing any JavaScript.
 *
 * Copy rules:
 *   - No fabricated statistics. Every claim maps 1-to-1 to src/utils/random.ts
 *     (crypto.getRandomValues) and src/utils/limits.ts (MAX_ENTRIES = 50 000).
 *   - Plain English at a reading age of 12–14.
 *   - No marketing superlatives.
 */

import React from 'react';
import { Cpu, Shuffle, Zap, Database, Lock } from 'lucide-react';
import { Link } from '../LocaleLink';

// ---------------------------------------------------------------------------
// Data — each spec item maps to a verifiable fact in the codebase
// ---------------------------------------------------------------------------

interface Spec {
  icon: React.ReactNode;
  term: string;      // <dt> — the property name
  value: string;     // <dd> — the measured value, bold
  detail: string;    // <dd> — plain-English explanation
}

const SPECS: Spec[] = [
  {
    icon: <Lock size={18} className="text-violet-500 flex-shrink-0 mt-0.5" aria-hidden="true" />,
    term: 'Random Number Source',
    value: 'Web Crypto where supported',
    detail: 'Uses window.crypto.getRandomValues() with rejection sampling when available, with a Math.random fallback for older environments.',
  },
  {
    icon: <Shuffle size={18} className="text-violet-500 flex-shrink-0 mt-0.5" aria-hidden="true" />,
    term: 'Selection Distribution',
    value: 'Uniform 1-in-N',
    detail: 'Equal-weight entries have the same configured chance on each spin; custom weights deliberately change those odds.',
  },
  {
    icon: <Zap size={18} className="text-violet-500 flex-shrink-0 mt-0.5" aria-hidden="true" />,
    term: 'Execution Environment',
    value: 'Client-side only',
    detail: 'The winner is selected in your browser. Analytics events omit wheel titles, entries, share codes, and results.',
  },
  {
    icon: <Database size={18} className="text-violet-500 flex-shrink-0 mt-0.5" aria-hidden="true" />,
    term: 'Maximum Entries',
    value: '50,000 names',
    detail: 'The wheel handles up to 50 000 entries per session and stores them locally unless you create a share link.',
  },
  {
    icon: <Cpu size={18} className="text-violet-500 flex-shrink-0 mt-0.5" aria-hidden="true" />,
    term: 'Result Timing',
    value: 'Pre-determined',
    detail: 'The winning entry is computed before the animation starts. The spin is a reveal, not the draw itself.',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders the Technical Precision & Fairness specs block.
 *
 * @param className - Optional wrapper class for spacing overrides.
 */
const TechnicalSpecs: React.FC<{ className?: string }> = ({ className = '' }) => (
  <section
    aria-labelledby="tech-specs-heading"
    className={`bg-gray-50 dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 py-12 ${className}`}
  >
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* ── Header ── */}
      <div className="mb-8 text-center">
        <h2
          id="tech-specs-heading"
          className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white"
        >
          Technical Precision &amp; Fairness
        </h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
          <strong className="font-semibold text-gray-700 dark:text-gray-200">TheNameWheel</strong> uses a{' '}
          <strong className="font-semibold text-gray-700 dark:text-gray-200">
            Web Crypto random number source in supported browsers
          </strong>
          {' '}with rejection sampling for an unbiased <strong className="font-semibold text-gray-700 dark:text-gray-200">1-in-N distribution</strong> on equal-weight spins.
        </p>
      </div>

      {/* ── Spec grid ──
           HTML spec for <dl>: direct children must be <dt>, <dd>, <div>,
           <script>, or <template>. Each <div> child may only contain <dt>
           and <dd> elements (plus <script>/<template>). The icon is moved
           inside the same wrapping <div> as its <dt>/<dd> siblings, styled
           with CSS (not a separate sibling element) so the accessibility
           tree remains well-formed. */}
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SPECS.map((spec) => (
          <div
            key={spec.term}
            className="
              p-5 rounded-2xl
              bg-white dark:bg-gray-900
              border border-gray-100 dark:border-gray-800
              hover:border-violet-200 dark:hover:border-violet-700
              hover:shadow-sm
              transition-all duration-150
            "
          >
            {/* Term — includes icon via CSS background trick; icon is
                aria-hidden so it does not pollute the description. */}
            <dt className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 leading-none mb-2">
              <span aria-hidden="true" className="text-violet-500 flex-shrink-0">{spec.icon}</span>
              {spec.term}
            </dt>
            {/* Value — the bold "headline" number/word */}
            <dd className="text-sm font-extrabold text-gray-900 dark:text-white leading-snug">
              {spec.value}
            </dd>
            {/* Detail — plain English explanation */}
            <dd className="mt-1 text-[12px] text-gray-500 dark:text-gray-400 leading-relaxed">
              {spec.detail}
            </dd>
          </div>
        ))}

        {/* ── Spanning CTA card — "Verify it yourself" ──
             Must contain <dt> and <dd> to be a valid <dl> child <div>. */}
        <div
          className="
            sm:col-span-2 lg:col-span-3
            p-5 rounded-2xl
            bg-violet-50 dark:bg-violet-950/30
            border border-violet-100 dark:border-violet-800/40
          "
        >
          <dt className="sr-only">Verification</dt>
          <dd className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-snug">
                Don&apos;t take our word for it — verify it yourself.
              </p>
              <p className="mt-0.5 text-[12px] text-gray-600 dark:text-gray-300 leading-relaxed">
                Open DevTools → Network, add a distinctive test entry, and confirm that its text is absent from requests. Analytics requests may appear. Or inspect{' '}
                <code className="text-[11px] bg-violet-100 dark:bg-violet-900/50 rounded px-1.5 py-0.5 font-mono text-violet-700 dark:text-violet-300">
                  crypto.getRandomValues
                </code>{' '}
                in the Sources panel.
              </p>
            </div>
            <Link
              to="/privacy/#how-we-proved-it"
              className="
                flex-shrink-0
                text-[13px] font-semibold
                text-violet-600 dark:text-violet-400
                hover:text-violet-800 dark:hover:text-violet-200
                hover:underline
                transition-colors
                whitespace-nowrap
              "
            >
              Step-by-step guide →
            </Link>
          </dd>
        </div>
      </dl>
    </div>
  </section>
);

export default TechnicalSpecs;
